import React, { forwardRef, useEffect, useImperativeHandle, useRef } from 'react'
import { useIntl } from 'react-intl'

import { Color } from '@tiptap/extension-color'
import { Document } from '@tiptap/extension-document'
import History from '@tiptap/extension-history'
import Paragraph from '@tiptap/extension-paragraph'
import Text from '@tiptap/extension-text'
import TextStyle from '@tiptap/extension-text-style'
import { EditorContent, useEditor } from '@tiptap/react'
import styled from 'styled-components'
import { useTheme } from 'styled-components/native'

import { convertTokenMessageToHtml, getLabelFromToken, MessageToken } from '@lyrahealth-inc/shared-app-logic'

import { getFontStyles } from '../../styles'
import { Span } from './extensions/span/SpanExtension'
import { Token } from './extensions/token/TokenExtension'

const PreservedText = Text.extend({
  parseHTML() {
    return [
      {
        tag: 'text',
        preserveWhitespace: 'full',
      },
    ]
  },

  renderHTML({ node }) {
    // Custom rendering logic to preserve spaces
    return node.text?.replace(/ /g, '\u00A0') || ''
  },
})

// Extended TextStyle to support class attributes
const ExtendedTextStyle = TextStyle.extend({
  addAttributes() {
    return {
      ...this.parent?.(),
      class: {
        default: null,
        parseHTML: (element) => element.getAttribute('class'),
        renderHTML: (attributes) => {
          if (!attributes.class) {
            return {}
          }
          return {
            class: attributes.class,
          }
        },
      },
    }
  },
})

export type TokenTextInputProps = {
  initialValue?: string
  onChange?: (text: string) => void
  readonly?: boolean
  customStyles?: React.CSSProperties
  fixedHeight?: string
}
export type TokenTextInputRef = {
  insertToken: (token: MessageToken) => void
}
const EditorContainer = styled.div<{ fixedHeight?: string }>(({ fixedHeight }) => ({
  border: '0px',
  minHeight: !!fixedHeight ? fixedHeight : 'auto',
}))
// this prevents 'customStyles' from being passed to the underlying dom component
const Editor = styled(EditorContent).withConfig({
  shouldForwardProp: (prop) => !['customStyles'].includes(prop),
})<{ customStyles?: React.CSSProperties }>(({ customStyles, readOnly }) => ({
  '.ProseMirror': {
    minHeight: '24px',
    outline: 'none',
    whiteSpace: 'normal',
    wordBreak: 'keep-all',
    overflowWrap: 'break-word',
    '& .error-text': {
      color: '#A63D51',
    },
    '&:focus': {
      outline: 'none',
    },
    '& p': {
      whiteSpace: 'normal',
      wordBreak: 'keep-all',
      overflowWrap: 'break-word',
      backgroundColor: 'white',
      margin: 0,
      outlineWidth: '0',
      outline: 'none',
      ...customStyles,
      ...(readOnly
        ? {
            overflow: 'hidden',
            textOverflow: 'ellipsis',
            display: '-webkit-box',
            WebkitLineClamp: '3',
            lineClamp: '3',
            WebkitBoxOrient: 'vertical',
          }
        : {}),
    },
  },
  'p:first-of-type > br.ProseMirror-trailingBreak': {
    display: 'none',
  },
  'p:last-of-type > .ProseMirror-trailingBreak': {
    display: 'none',
  },
}))

export const TokenTextInput = forwardRef<TokenTextInputRef, TokenTextInputProps>(
  ({ initialValue, onChange, readonly, customStyles, fixedHeight }, ref) => {
    const theme = useTheme()
    const intl = useIntl()
    const lastValueRef = useRef<string | undefined>()

    const editor = useEditor({
      extensions: [
        Document,
        PreservedText,
        Paragraph,
        History,
        Token,
        ExtendedTextStyle,
        Span,
        Color.configure({
          types: ['textStyle'], // Ensures color applies as an inline style
        }),
      ],
    })

    useEffect(() => {
      if (initialValue && editor) {
        const { from, to } = editor.state.selection

        editor.chain().setContent(convertTokenMessageToHtml(initialValue, intl)).setTextSelection({ from, to }).run()
      }
    }, [initialValue, editor, intl])

    useEffect(() => {
      editor?.setOptions({ editable: !readonly })
    }, [editor, readonly])

    useImperativeHandle(ref, () => ({
      insertToken: (token: MessageToken) => {
        editor?.commands.insertContent({
          type: 'token',
          attrs: {
            label: intl.formatMessage(getLabelFromToken(token)),
            name: token,
          },
        })
      },
    }))

    useEffect(() => {
      const onUpdate = () => {
        const currentValue = editor?.getHTML()
        if (lastValueRef.current === currentValue) {
          return
        }
        let stripped = currentValue
        stripped = stripped?.replace(/^(?:\s*<p><\/p>)+/, '')
        stripped = stripped?.replace(/^(?:\s*<p><br(?:\s+class="[^"]*")?\s*\/?><\/p>)+/, '')
        stripped = stripped?.replaceAll(/<p><br(?:\s+class="[^"]*")?\s*\/?><\/p>/g, '')
        stripped = stripped?.replace(/(?:<p><\/p>\s*)+$/, '')
        lastValueRef.current = currentValue
        onChange?.(stripped?.replaceAll('\n', '') ?? '')
      }
      if (editor && !readonly) {
        editor.on('transaction', onUpdate)
      }
      return () => {
        editor?.off('transaction', onUpdate)
      }
    }, [editor, onChange, readonly])

    const handleContainerClick = () => {
      if (editor && !readonly && editor.getText().trim() === '') {
        editor.commands.focus()
      }
    }

    return (
      <EditorContainer data-testid={'TokenTextInput'} fixedHeight={fixedHeight} onClick={handleContainerClick}>
        <Editor
          editor={editor}
          style={{
            ...getFontStyles(theme.colors, theme.fontFamilyMetadata).body.default,
            lineHeight: theme.spacing['24px'],
          }}
          readOnly={readonly}
          customStyles={customStyles}
        />
      </EditorContainer>
    )
  },
)
